"use client";

/**
 * OnDemandIndexingLive — the on-demand indexing story, read from the running system.
 *
 * The point of this block is a single contrast: a page submitted directly is searchable in about
 * half a minute, while a page we discover on our own waits hours behind everything else already
 * queued. Both numbers come from the same timestamps on the same rows, so the comparison is
 * honest rather than a best case set against an average.
 *
 * Numbers are polled from /search/time-to-index, NOT hardcoded. A figure typed into marketing copy
 * is true on the day it is written and quietly wrong forever after — and it freezes into every
 * cached copy and AI summary of the page. Degrades to an em dash if the endpoint is unreachable.
 */
import { useEffect, useState } from "react";
import { Zap, Clock, Globe, FileText } from "lucide-react";

/* Exactly what /search/time-to-index returns. The previous shape declared four fields the endpoint
   has never sent, which is how two cards came to render a dash permanently. */
type TTI = {
  on_demand_n?: number;
  on_demand_s?: number;
  crawled_n?: number;
  crawled_s?: number;
  rendered_n?: number;
  rendered_s?: number;
};

const TTI_URL = "https://extension.askfinz.ai/api/v1/search/time-to-index";

/* Durations here run from seconds to half a day, so a single unit is unreadable at one end.
   Step the unit with the magnitude and keep a decimal only while it still says something. */
function dur(s?: number) {
  if (!s || !isFinite(s) || s <= 0) return "—";
  if (s < 90) return `${Math.round(s)}s`;
  const m = s / 60;
  if (m < 90) return `${m.toFixed(m < 10 ? 1 : 0)}m`;
  const h = m / 60;
  return `${h.toFixed(h < 10 ? 1 : 0)}h`;
}

export function OnDemandIndexingLive({ initial }: { initial?: TTI | null }) {
  /* Seeded by the server so the figure is in the HTML on first paint rather than replacing a
     dash after hydration. The client fetch below stays, to refresh a prerendered page. */
  const [d, setD] = useState<TTI | null>(initial ?? null);

  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        // Cache-buster, not belt-and-braces: Cloudflare caches /api responses on this estate even
        // when the origin sends no-store, and it will happily pin the empty cold-start body for the
        // full TTL — which is exactly how this endpoint served {} for minutes while the origin was
        // healthy. A unique URL per request is the pattern the rest of the estate already uses.
        const r = await fetch(`${TTI_URL}?_t=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const j = (await r.json()) as TTI;
        /* Guard on a field the endpoint really sends — `pages` never existed here,
           so this condition was false on every poll and the card never refreshed. */
        if (on && j && typeof j.on_demand_s === "number") setD(j);
      } catch {
        /* transient — keep the last good values */
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  /* How many times faster asking is than waiting. Computed, not written down, so it stays true as
     the backlog grows and shrinks. */
  const speedup =
    d && typeof d.on_demand_s === "number" && d.on_demand_s > 0 && typeof d.crawled_s === "number"
      ? Math.round(d.crawled_s / d.on_demand_s)
      : null;

  return (
    <div>
      <div className="flex items-baseline gap-3">
        <span className="display-serif text-6xl md:text-7xl leading-none tabular-nums">
          {dur(d?.on_demand_s)}
        </span>
        <span className="text-foreground/65 text-sm">
          from asking to searchable
        </span>
      </div>
      <p className="mt-4 text-foreground/70 leading-relaxed max-w-xl">
        Ask for a page and it goes to the front of the queue. It doesn&#39;t wait
        behind the pages we&#39;re already working through
        {speedup ? (
          <>
            {" "}
            &mdash; about <strong className="text-foreground">{speedup}&times; faster</strong>{" "}
            than waiting for us to find it
          </>
        ) : null}
        .
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-foreground/10 border hairline">
        {[
          {
            icon: <Zap className="h-4 w-4" aria-hidden />,
            v: dur(d?.on_demand_s),
            l: "When you ask for it",
            hi: true,
          },
          {
            icon: <Clock className="h-4 w-4" aria-hidden />,
            v: dur(d?.crawled_s),
            l: "When we find it ourselves",
          },
          {
            icon: <Zap className="h-4 w-4" aria-hidden />,
            /* The comparison this section is about, finally as a figure rather than as prose. */
            v: speedup != null ? `${speedup}×` : "—",
            l: "Faster than waiting to be found",
          },
          {
            icon: <Globe className="h-4 w-4" aria-hidden />,
            v: dur(d?.rendered_s),
            l: "When the page needs a browser",
          },
        ].map((s) => (
          <div key={s.l} className="bg-background p-5">
            <div
              className={
                s.hi ? "text-emerald-500/90" : "text-foreground/45"
              }
            >
              {s.icon}
            </div>
            <div
              className={`mt-3 font-mono text-2xl tabular-nums ${
                s.hi ? "text-emerald-500/90" : ""
              }`}
            >
              {s.v}
            </div>
            <div className="mt-1 text-xs text-foreground/60 leading-snug">
              {s.l}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-5 font-mono text-xs text-foreground/50 leading-relaxed">
        Measured, not estimated. Every page records the moment it arrived and the
        moment it became searchable
        {d?.on_demand_n
          ? ` — across ${d.on_demand_n.toLocaleString()} asked for`
          : ""}
        {d?.crawled_n ? ` and ${d.crawled_n.toLocaleString()} found by crawling` : ""} over
        the last two days.
      </p>
    </div>
  );
}
